import React, { useState, useEffect } from "react";
import { useUserInfo } from "../../../../context/user";
import { toast } from "react-hot-toast";
import { Switch } from "components/ui/switch";
import { useGuardrails, useUpdateGuardrails } from "../../../../queries/guardrails";

const Guardrails: React.FC = () => {
  const [piiEnabled, setPiiEnabled] = useState(false);
  const userInfo = useUserInfo();
  const userActiveLocation = userInfo[4];
  const { data, isLoading: loading } = useGuardrails(userActiveLocation?.id);
  const { mutateAsync: updateGuardrails } = useUpdateGuardrails(userActiveLocation?.id);

  useEffect(() => {
    if (data) {
      setPiiEnabled(data.pii_protection_enabled || false);
    }
  }, [data]);

  const handleToggle = async () => {
    if (!userActiveLocation?.id) {
      toast.error("Profile not found");
      return;
    }

    const newValue = !piiEnabled;
    setPiiEnabled(newValue);

    try {
      const response = await updateGuardrails({ piiEnabled: newValue });

      if (response?.success) {
        toast.success(
          response.message || `PII protection ${newValue ? "enabled" : "disabled"}`,
        );
      } else {
        setPiiEnabled(!newValue);
        toast.error(response?.message || "Failed to update guardrails");
      }
    } catch (error: any) {
      setPiiEnabled(!newValue);
      toast.error(error?.response?.data?.message || "An unexpected error occurred");
      console.error(error);
    }
  };

  if (loading) {
    return <div className="max-w-full text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="max-w-full">
      <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-4">
        <span className="page-eyebrow">Configure</span>
        <h1 className="page-display">
          Guardrails. <span className="accent block md:inline">Keep your agents safe and on task.</span>
        </h1>
        <p className="page-lede">
          Detect and redact personally identifiable information, block jailbreak attempts, and keep responses on brand. Guardrails apply to every Task across Maybe*.
        </p>
      </div>

      <div className="space-y-4">
        {/* Privacy Protection Card */}
        <div className="bg-card border border-border rounded-md p-8 mb-4">
          <div>
            <h2 className="text-[22px] font-bold text-foreground mb-2">
              Privacy Protection
            </h2>
            <p className="text-muted-foreground text-sm mb-8">
              Configure automatic detection and redaction of sensitive information
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1 pr-4">
              <h3 className="text-[15px] font-bold text-foreground mb-1">
                Personally Identifiable Information
              </h3>
              <p className="text-muted-foreground text-sm leading-snug">
                Automatically detect and redact PII such as names, email addresses, phone numbers, and social security numbers
              </p>
            </div>
            <div className="flex-shrink-0">
              <Switch checked={piiEnabled} onCheckedChange={() => handleToggle()} />
            </div>
          </div>
        </div>

        {/* Jailbreak Detection Card */}
        <div className="bg-card border border-border rounded-md p-8 mb-4">
          <div>
            <h2 className="text-[22px] font-bold text-foreground mb-2">
              Jailbreak Detection
            </h2>
            <p className="text-muted-foreground text-sm mb-8">
              Prevent attempts to bypass agent instructions and security controls
            </p>
          </div>

          <div>
            <h3 className="text-[15px] font-bold text-foreground mb-1">Jailbreak</h3>
            <p className="text-muted-foreground text-sm leading-snug">
              All agents detect jailbreaking attempts to ensure the agent stays on task
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guardrails;
